import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { sentry } from '../../../../sentry';
import { ArchiveLocationSectorInput, UnarchiveLocationSectorInput } from '../../api/location-sectors/location-sectors.type';
import { CustomMessagesService } from '../../errors/custom-messages.service';
import { delay } from '../../utils/delay';
import { ArchiveLocationSectorApi } from './archive-location-sectors.api';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface ArchiveSectorDialogData {
  sectorId: string;
  archived: boolean;
  name: string;
}

export interface ArchiveSectorDialogResult {
  reload: boolean;
}

type ArchiveSectorDialogSelection = 'ARCHIVE_ENVIRONMENTS' | 'UNARCHIVE_ENVIRONMENTS' | 'ARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS' | 'UNARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS';

@Component({
  templateUrl: './archive-sector-dialog.component.html',
  styleUrls: ['./archive-sector-dialog.component.scss']
})
export class ArchiveSectorDialogComponent {

  public selected: ArchiveSectorDialogSelection = null;

  public isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: ArchiveSectorDialogData,
    private readonly matDialogRef: MatDialogRef<ArchiveSectorDialogComponent, ArchiveSectorDialogResult>,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly archiveLocationSectorApi: ArchiveLocationSectorApi,
    private readonly messageService: CustomMessagesService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async updateArchived(): Promise<void> {
    try {
      this.isLoading = true;
      if (this.data.archived) {
        await Promise.all([
          this.archiveLocationSectorApi.unarchive(this.prepareUnarchivePayload()),
          delay(1000)
        ]);
        this.messageService.success('ARCHIVE_SECTOR_DIALOG.UNARCHIVE.SUCCESS');
      } else {
        await Promise.all([
          await this.archiveLocationSectorApi.archive(this.prepareArchivePayload()),
          delay(1000)
        ]);
        this.messageService.success('ARCHIVE_SECTOR_DIALOG.ARCHIVE.SUCCESS');
      }
    } catch (err) {
      sentry.captureException(err);
      this.messageService.error('UNKNOWN_ERROR');
    } finally {
      this.matDialogRef.close({ reload: true });
    }

  }

  prepareArchivePayload(): ArchiveLocationSectorInput {
    return {
      id: this.data.sectorId,
      archiveEquipments: this.selected === 'ARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS',
    };
  }

  prepareUnarchivePayload(): UnarchiveLocationSectorInput {
    return {
      id: this.data.sectorId,
      unarchiveEquipments: this.selected === 'UNARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS',
    };
  }
}
